import { relative } from "node:path";
import type {
  DriftIssue,
  ProjectModel,
  WorkspaceDependencyEdge,
  WorkspaceInfo,
} from "../../types.js";
import { readCapped } from "./shared.js";

interface DocumentedWorkspaceEdge {
  file: string;
  line: number;
  from: string;
  to: string;
  snippet: string;
}

interface SuggestedWorkspaceDoc {
  section: string;
  line: string;
}

const RELATIONSHIP_HINT =
  /\b(depends on|uses|imports|consumes|shares|shared with|backed by|powered by|built on top of)\b/i;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{3,}/;

export function checkWorkspaceDependencies(
  scaffoldFiles: string[],
  project: ProjectModel,
  projectRoot: string
): DriftIssue[] {
  if (project.workspaces.length < 2 || project.workspaceDependencies.length === 0) {
    return [];
  }

  const documentedEdges = extractDocumentedWorkspaceEdges(scaffoldFiles, project.workspaces);
  const documentedKeys = new Set(documentedEdges.map(toEdgeKey));
  const actualKeys = new Set(project.workspaceDependencies.map(toEdgeKey));
  const issues: DriftIssue[] = [];
  const preferredDocFile = pickPreferredWorkspaceDoc(scaffoldFiles, projectRoot);

  for (const edge of project.workspaceDependencies) {
    if (documentedKeys.has(toEdgeKey(edge))) continue;
    const suggestion = buildWorkspaceSuggestion(edge);
    issues.push({
      code: "WORKSPACE_DEPENDENCY_MISSING",
      severity: "warning",
      file: preferredDocFile,
      line: null,
      message: `Workspace dependency is not documented in scaffold: ${edge.from} -> ${edge.to}`,
      explanation:
        `The repo contains an internal workspace ${edge.type} from ${edge.from} to ${edge.to}, ` +
        `derived from manifest dependency "${edge.dependencyName}", but no scaffold line mentions that relationship.`,
      suggestion: `Add under ${suggestion.section}: ${suggestion.line}`,
    });
  }

  for (const documented of documentedEdges) {
    if (actualKeys.has(toEdgeKey(documented))) continue;
    issues.push({
      code: "WORKSPACE_DEPENDENCY_INVALID",
      severity: "warning",
      file: relative(projectRoot, documented.file),
      line: documented.line,
      message: `Documented workspace dependency does not exist: ${documented.from} -> ${documented.to}`,
      explanation:
        `This scaffold line suggests an internal dependency relation, but the workspace manifests do not define it: "${documented.snippet}". ` +
        "The checker only accepts relations backed by actual internal workspace dependencies.",
    });
  }

  return issues;
}

function extractDocumentedWorkspaceEdges(
  scaffoldFiles: string[],
  workspaces: WorkspaceInfo[]
): DocumentedWorkspaceEdge[] {
  const aliases = buildWorkspaceAliases(workspaces);
  const edges: DocumentedWorkspaceEdge[] = [];

  for (const file of scaffoldFiles) {
    const content = readCapped(file, 5_000);
    if (content === null) continue;

    const lines = content.split("\n");
    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      if (!lineHasRelationshipHint(line)) continue;

      const matched = aliases
        .map((entry) => {
          const match = line.match(entry.pattern);
          return match?.index !== undefined
            ? { workspace: entry.workspace, index: match.index }
            : null;
        })
        .filter((entry): entry is { workspace: WorkspaceInfo; index: number } => Boolean(entry))
        .sort((a, b) => a.index - b.index);

      const ordered = dedupeWorkspaceOrder(matched.map((entry) => entry.workspace));
      if (ordered.length < 2) continue;

      const [from, ...targets] = ordered;
      for (const to of targets) {
        if (from.path === to.path) continue;
        edges.push({
          file,
          line: index + 1,
          from: from.path,
          to: to.path,
          snippet: line.trim(),
        });
      }
    }
  }

  return dedupeDocumentedEdges(edges);
}

function buildWorkspaceAliases(workspaces: WorkspaceInfo[]): Array<{ workspace: WorkspaceInfo; pattern: RegExp }> {
  return workspaces.flatMap((workspace) => {
    const identifiers = new Set<string>([workspace.path]);
    if (workspace.manifest.name) identifiers.add(workspace.manifest.name);
    const basename = workspace.path.split("/").pop();
    if (basename) identifiers.add(basename);

    return [...identifiers]
      .filter(Boolean)
      .map((identifier) => ({
        workspace,
        pattern: new RegExp(`(^|[^A-Za-z0-9@/_-])${escapeRegExp(identifier)}([^A-Za-z0-9@/_-]|$)`, "i"),
      }));
  });
}

function dedupeDocumentedEdges(edges: DocumentedWorkspaceEdge[]): DocumentedWorkspaceEdge[] {
  const seen = new Set<string>();
  const deduped: DocumentedWorkspaceEdge[] = [];
  for (const edge of edges) {
    const key = `${edge.file}:${edge.line}:${edge.from}:${edge.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(edge);
  }
  return deduped;
}

function dedupeWorkspaceOrder(workspaces: WorkspaceInfo[]): WorkspaceInfo[] {
  const seen = new Set<string>();
  return workspaces.filter((workspace) => {
    if (seen.has(workspace.path)) return false;
    seen.add(workspace.path);
    return true;
  });
}

function lineHasRelationshipHint(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (TABLE_DIVIDER.test(trimmed)) return false;
  // Only use explicit relationship words ("depends on", "uses", "imports", etc.)
  // Arrow-only lines (→, ->) are too ambiguous — they appear in diagrams and architecture
  // descriptions without implying npm-level workspace dependencies.
  return RELATIONSHIP_HINT.test(line);
}

function pickPreferredWorkspaceDoc(scaffoldFiles: string[], projectRoot: string): string {
  const preferredSuffixes = [
    "context/architecture.md",
    "context/conventions.md",
    "context/stack.md",
    "ROUTER.md",
  ];

  for (const suffix of preferredSuffixes) {
    const match = scaffoldFiles.find((file) => file.endsWith(suffix));
    if (match) return relative(projectRoot, match);
  }

  return "ROUTER.md";
}

function buildWorkspaceSuggestion(edge: WorkspaceDependencyEdge): SuggestedWorkspaceDoc {
  const section = "## Workspace Relationships";
  const relation =
    edge.type === "devDependency"
      ? `${edge.from} depends on ${edge.to} for development and tooling shared through \`${edge.dependencyName}\`.`
      : `${edge.from} uses ${edge.to} via internal package \`${edge.dependencyName}\`.`;

  return {
    section,
    line: `- ${relation}`,
  };
}

function toEdgeKey(edge: Pick<WorkspaceDependencyEdge, "from" | "to">): string {
  return `${edge.from}->${edge.to}`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
